import React, { useState, useEffect } from 'react';
import axios from 'axios';
import './BaseDashboard.css';

const FlexibleDashboard = ({ 
  title, 
  user, 
  onLogout: _onLogout, 
  children,
  loading = false,
  defaultActiveTab,
  onTabChange,
  customTabs = null,
  tabBadges = {},
  banner = null
}) => {
  const [activeTab, setActiveTab] = useState(defaultActiveTab || 'overview');
  const [isOnline, setIsOnline] = useState(navigator.onLine);

  // Extract tabs from children or use custom tabs
  const tabs = customTabs || React.Children.map(children, child => {
    if (React.isValidElement(child) && child.props.tabId) {
      return {
        id: child.props.tabId,
        label: child.props.tabLabel || child.props.tabId,
        component: child
      };
    }
    return null;
  }).filter(Boolean);

  // Update activeTab when defaultActiveTab prop changes
  useEffect(() => {
    if (defaultActiveTab) {
      setActiveTab(defaultActiveTab);
    }
  }, [defaultActiveTab]);

  useEffect(() => {
    const handleOnlineStatus = () => {
      setIsOnline(navigator.onLine);
    };

    window.addEventListener('online', handleOnlineStatus);
    window.addEventListener('offline', handleOnlineStatus);

    return () => {
      window.removeEventListener('online', handleOnlineStatus);
      window.removeEventListener('offline', handleOnlineStatus);
    };
  }, []);



  const handleTabChange = (tabId) => {
    // Always update the local state
    setActiveTab(tabId);
    
    // If onTabChange prop is provided, also call it for custom tab handling
    if (onTabChange) {
      onTabChange(tabId);
    }
  };

  const handleLogout = async () => {
    try {
      await axios.post('/api/logout', {}, {
        withCredentials: true
      });
      
      // Force a page reload to clear any cached state
      window.location.href = '/login';
    } catch (error) {
      console.error('Error logging out:', error);
      console.error('Error response:', error.response?.data);
      // Even if logout fails, redirect to login
      window.location.href = '/login';
    }
  };

  // Get the appropriate code for the subtitle
  const getUserCode = () => {
    // Check user type first to determine which code to show
    if (user.userType === 'student' && user.studentCode) {
      return { label: 'Code', value: user.studentCode };
    } else if (user.userType === 'teacher' && (user.teacherCode || user.tchCode)) {
      return { label: 'Teacher Code', value: user.teacherCode || user.tchCode };
    } else if (user.isSchAdmin && user.schoolCode) {
      return { label: 'School Code', value: user.schoolCode };
    } else if (user.isOrgAdmin && (user.orgCode || user.code)) {
      return { label: 'Org Code', value: user.orgCode || user.code };
    } else if (user.teacherCode || user.tchCode) {
      return { label: 'Teacher Code', value: user.teacherCode || user.tchCode };
    } else if (user.studentCode) {
      return { label: 'Code', value: user.studentCode };
    }
    return null;
  };

  if (loading) {
    return (
      <div className="dashboard-loading">
        <div className="spinner-border text-light" role="status">
          <span className="visually-hidden">Loading...</span>
        </div>
        <p>Loading dashboard...</p>
      </div>
    );
  }

  if (!user) {
    return <div>Loading...</div>;
  }

  return (
    <div className="base-dashboard">
      <div className="dashboard-header">
        <h1 className="dashboard-title">{title}</h1>
        <p className="dashboard-subtitle">
          Welcome back, {user.contactName} - {(() => {
            const codeObj = getUserCode();
            if (codeObj) {
              return `${codeObj.label}: ${codeObj.value}`;
            }
            return '';
          })()}
        </p>
      </div>

      {!isOnline && (
        <div className="alert alert-warning text-center">You are currently offline</div>
      )}

      {banner}

      <div className="dashboard-content-wrapper">
        <div className="d-flex justify-content-between align-items-center mb-4">
          <ul className="nav nav-tabs">
            {tabs.map((tab) => (
              <li className="nav-item" key={tab.id}>
                <button 
                  className={`nav-link ${activeTab === tab.id ? 'active' : ''}`} 
                  onClick={() => handleTabChange(tab.id)}
                >
                  {tab.label}
                  {tabBadges[tab.id] && tabBadges[tab.id] > 0 && (
                    <span className="badge bg-danger ms-2">{tabBadges[tab.id]}</span>
                  )}
                </button>
              </li>
            ))}
          </ul>
          <ul className="nav nav-tabs">
            <li className="nav-item">
              <button className="nav-link" onClick={handleLogout}>
                Logout
              </button>
            </li>
          </ul>
        </div>

        <div className="tab-content">
          {children && React.Children.map(children, (child, _index) => {
            if (!child || !React.isValidElement(child)) {
              return null;
            }
            
            if (child.props?.tabId === activeTab) {
              return React.cloneElement(child, { 
                user, 
                isOnline,
                activeTab,
                isActive: true
              });
            }
            return null;
          })}
        </div>
      </div>
    </div>
  );
};

export default FlexibleDashboard; 